import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { estApi } from "@/estimating/lib/api";

// A contact typed in a grid that the Directory doesn't know → offer to add it under the row's company.
export const offerDirectoryAdd = async ({ email, name = "", phone = "", companyName = "", type = "gc" }) => {
  const em = (email || "").trim().toLowerCase();
  if (!em || !em.includes("@")) return;
  try {
    const found = await estApi.getContacts({ search: em, limit: 5 });
    if ((found.contacts || found.items || []).some((c) => (c.email || "").toLowerCase() === em)) return;
  } catch { return; }
  toast(`${em} isn't in the Directory`, {
    description: companyName ? `Add as a contact at ${companyName}?` : "Add as a new contact?",
    action: { label: "Add to Directory", onClick: async () => {
      try {
        let company = null;
        if (companyName) {
          const cs = await estApi.getCompanies({ search: companyName, limit: 5 });
          company = (cs.companies || []).find((c) => c.name.toLowerCase() === companyName.toLowerCase()) || null;
          if (!company) company = await estApi.createCompany({ name: companyName, company_type: type, types: [type] });
        }
        const [first = "", ...rest] = (name || "").trim().split(" ");
        await estApi.createContact({ company_id: company?.id || null, first_name: first, last_name: rest.join(" "), email: em,
                                     phone: phone || "", role: type === "gc" ? "estimating" : "other" });
        toast.success(`${em} added to the Directory${company ? ` · ${company.name}` : ""}`);
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that contact"); }
    } },
    duration: 12000,
  });
};

export const bareInput = "w-40 bg-transparent px-0 py-0.5 text-xs outline-none border-0 border-b border-transparent focus:border-[#007BFF] placeholder:text-muted-foreground/60";

/** Contact read-out: name over a mailto (and tel) link. */
export const ContactLines = ({ name, email, phone, testid }) => (
  <div className="flex min-w-0 flex-col">
    {name && <span className="truncate text-xs text-foreground" data-testid={`${testid}-name`}>{name}</span>}
    {email && <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()} className="truncate text-[11px] text-sky-700 hover:underline dark:text-sky-400"
                 data-testid={`${testid}-email`}>{email}</a>}
    {phone && <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()} className="truncate text-[11px] text-sky-700 hover:underline dark:text-sky-400"
                 data-testid={`${testid}-phone`}>{phone}</a>}
  </div>
);

/** Borderless contact cell (Basis of Design standard): "—" when empty, click anywhere to edit inline.
 *  `phone` undefined → no phone field. onSave({name, email, phone}) fires once when the editor closes with changes. */
export function ContactCell({ name = "", email = "", phone, onSave, testid, readOnly = false, directory }) {
  const withPhone = phone !== undefined;
  const [edit, setEdit] = useState(null);
  const open = () => !readOnly && setEdit({ name, email, phone: phone || "" });
  const set = (k) => (e) => setEdit((d) => ({ ...d, [k]: e.target.value }));
  const commit = () => {
    if (!edit) return;
    const d = { name: edit.name.trim(), email: edit.email.trim(), ...(withPhone ? { phone: edit.phone.trim() } : {}) };
    setEdit(null);
    const cur = { name, email, phone: phone || "" };
    if (!Object.keys(d).some((k) => d[k] !== cur[k])) return;
    onSave(d);
    if (d.email && d.email !== email && directory) offerDirectoryAdd({ ...directory, ...d });
  };
  if (edit) {
    return (
      <div className="flex flex-col gap-0.5" data-testid={`${testid}-editor`}
           onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) commit(); }}
           onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEdit(null); }}>
        <input autoFocus value={edit.name} onChange={set("name")} placeholder="Name" className={bareInput} data-testid={`${testid}-name-input`} />
        <input value={edit.email} onChange={set("email")} placeholder="email@company.com" type="email" className={`${bareInput} text-[11px]`} data-testid={`${testid}-email-input`} />
        {withPhone && <input value={edit.phone} onChange={set("phone")} placeholder="Phone" type="tel" className={`${bareInput} text-[11px]`} data-testid={`${testid}-phone-input`} />}
      </div>
    );
  }
  if (!name && !email && !phone) {
    return <button type="button" onClick={open} disabled={readOnly} title={readOnly ? undefined : "Add contact"} data-testid={`${testid}-add`}
                   className="block w-full min-w-[6rem] py-1 text-left text-muted-foreground hover:text-foreground disabled:hover:text-muted-foreground">—</button>;
  }
  return (
    <div className={`group flex w-full items-start gap-1 py-0.5 leading-tight ${readOnly ? "" : "cursor-text"}`} onClick={open}
         title={readOnly ? undefined : "Click to edit"} data-testid={testid}>
      <ContactLines name={name} email={email} phone={phone} testid={testid} />
      {!readOnly && <Pencil className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />}
    </div>
  );
}
